import { useMutation, useQueryClient } from "@tanstack/react-query";
import { OAuthProvider, signInWithPopup } from "firebase/auth";

import { auth, forceInMemorySession } from "@/lib/firebase/firebase-client";

import { fetchJson } from "@/dashboard/hooks/api/fetch-json";

interface SignInWithOktaArgs {
  googleIdpTenantId: string;
  oktaProviderId: string; // e.g., "oidc.okta" - configured in Firebase Console
}

export function useSignInWithOkta() {
  const qc = useQueryClient();

  return useMutation<void, Error, SignInWithOktaArgs>({
    mutationFn: async ({ googleIdpTenantId, oktaProviderId }) => {
      console.log("[OKTA DEBUG] Starting Okta sign-in flow", {
        googleIdpTenantId,
        oktaProviderId,
      });

      // Ensure correct tenant
      const previousTenant = auth.tenantId;
      auth.tenantId = googleIdpTenantId;

      try {
        // Memory-only persistence
        console.log("[OKTA DEBUG] Setting in-memory session persistence");
        await forceInMemorySession();
        console.log("[OKTA DEBUG] In-memory session set");

        // Firebase OIDC sign-in via popup
        console.log("[OKTA DEBUG] Opening Okta popup...");
        const provider = new OAuthProvider(oktaProviderId);
        const { user } = await signInWithPopup(auth, provider);
        console.log("[OKTA DEBUG] Popup sign-in completed", {
          userId: user.uid,
          email: user.email,
        });

        console.log("[OKTA DEBUG] Getting ID token...");
        const idToken = await user.getIdToken();
        console.log("[OKTA DEBUG] ID token obtained", {
          tokenLength: idToken.length,
        });

        // Tell the server to mint/refresh the session cookie
        // Server will handle JIT provisioning if needed
        console.log("[OKTA DEBUG] Posting to /api/dashboard/session...");
        await fetchJson("/api/dashboard/session", {
          method: "POST",
          body: JSON.stringify({ idToken }),
        });
        console.log("[OKTA DEBUG] Session API call completed successfully");
      } catch (err) {
        console.error("[OKTA DEBUG] Error in mutation:", err);
        throw err;
      } finally {
        // Restore previous tenant
        auth.tenantId = previousTenant;
        console.log("[OKTA DEBUG] Restored previous tenant");
      }
    },
    onSuccess: async () => {
      console.log("[OKTA DEBUG] onSuccess callback started");
      try {
        // Guarantees AuthProvider sees fresh user immediately
        console.log("[OKTA DEBUG] Invalidating 'me' query...");
        await qc.invalidateQueries({ queryKey: ["me"] });
        console.log("[OKTA DEBUG] 'me' query invalidated");

        // Clear any stale call-source-status from previous user
        console.log("[OKTA DEBUG] Removing call-source-status queries");
        qc.removeQueries({ queryKey: ["call-source-status"] });
        console.log("[OKTA DEBUG] onSuccess callback completed");
      } catch (err) {
        console.error("[OKTA DEBUG] Error in onSuccess:", err);
        throw err;
      }
    },
    onError: (error) => {
      console.error("[OKTA DEBUG] Mutation failed with error:", error);
    },
  });
}
